<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      v-if="variant === 'active' && isAdmin"
      :icon="AddIcon"
      :label="$t('pages.mountings.mounting.timeslots.modal.new.buttonText')"
      @click="mountingTimeslotModalOpen = true"
    />
    <export
      url="/mounting_timeslots/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    >
      <ExportLink
        url="/mounting_timeslot_registrations/csv"
        :filter="timeslotFilter"
        :columns="timeslotRegistrationColumns"
        :visible-columns="visibleTimeslotRegistrationColumns"
        :title="registrationCsvTitle"
        :label="$t('pages.mountings.mounting.timeslots.downloadAllRegistrations')"
        class="mt-5"
        sort-key="timeslot.startTime"
        sort-direction="ASC"
      />
      <ExportLink
        url="/mounting_timeslot_registrations/csv"
        :filter="timeslotFilter"
        :columns="timeslotRegistrationColumns"
        :visible-columns="visibleTimeslotRegistrationColumns"
        :title="registrationCsvTitle"
        :label="$t('pages.mountings.mounting.timeslots.downloadAllRegistrationsXlsx')"
        class="mt-5"
        sort-key="timeslot.startTime"
        sort-direction="ASC"
        format="xlsx"
      />
    </export>
  </toolbar>
  <mounting-timeslot-modal
    v-model:open="mountingTimeslotModalOpen"
    :mounting="modelValue"
    v-model:mounting-timeslot="editedMountingTimeslot"
    @saved="refetch"
  />
  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :row-link="
          (item) => ({
            name: 'mounting-timeslot-registrations-pending',
            params: { mountingTimeslotId: item.id },
          })
        "
        :visible-columns="visibleColumns"
        :actions="isAdmin ? actions : undefined"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import AddIcon from '@images/panes/add.svg';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Action from '@utils/toolbar/Action.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { HydraCollection, Mounting, MountingTimeslot } from '@/entities/Entities';

import { useNotify } from '@utils/notifications';
import { useDelete } from '@hooks/api';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import MountingTimeslotModal from '@pages/mountings/mounting/MountingTimeslotModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useStore } from '@/store';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  modelValue: Mounting | null;
  variant: 'active' | 'closed';
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'startTime');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const csvTitle = computed(() => {
  return t(`csv.title.mountingTimeslotsForMounting.${props.variant}`, {
    mounting: props.modelValue?.address,
  });
});

const registrationCsvTitle = computed(() => {
  return t(`csv.title.mountingTimeslotRegistrationsForMounting`, {
    mounting: props.modelValue?.address,
  });
});

const mountingTimeslotModalOpen = ref(false);
const editedMountingTimeslot = ref();

const [deleteMountingTimeslot, { loading: deleting }] = useDelete();

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  mounting: props.modelValue?.id ?? '',
  closed: props.variant === 'closed',
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const timeslotFilter = computed(() => ({
  'timeslot.mounting': props.modelValue?.id ?? '',
  status: 'pending',
  approved: false,
  ...Object.fromEntries(
      Object.entries(filter.value).map(([key, value]) =>
        // Modify key here
        [`timeslot.${key}`, value]
      )
    )
  }),
);

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const actions = [
  {
    title: t('pages.mountings.mounting.timeslots.actions.edit'),
    action: (item: MountingTimeslot) => {
      mountingTimeslotModalOpen.value = true;
      editedMountingTimeslot.value = { ...item };
    },
    condition: (item: any) => isAdmin,
  },
  {
    title: t('pages.mountings.mounting.timeslots.actions.delete'),
    action: async (item: MountingTimeslot) => {
      if (await deleteMountingTimeslot(`/mounting_timeslots/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
    condition: (item: any) => isAdmin,
  },
];

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.mountings.mounting.timeslots.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'startTime',
    title: t('mountingTimeslot.startTime'),
    group: t('pages.mountings.mounting.timeslots.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        label: t('mountingTimeslot.startTime'),
      },
    },
  },
  {
    key: 'duration',
    title: t('mountingTimeslot.duration'),
    group: t('pages.mountings.mounting.timeslots.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('mountingTimeslot.duration'),
      },
    },
  },
  {
    key: 'bookingNumbers',
    title: t('mountingTimeslot.bookingNumbers'),
    group: t('pages.mountings.mounting.timeslots.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (value: { booked: string; slots: string }) => `${value.booked} / ${value.slots}`,
    filter: {
      key: 'size',
      type: 'range',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        label: t('mountingTimeslot.size'),
      },
    },
  },
  {
    key: 'active',
    csvTranslations: {
      true: t('mountingTimeslot.active.true'),
      false: t('mountingTimeslot.active.false'),
    },
    title: t('mountingTimeslot.active.DEFAULT'),
    group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => t(`mountingTimeslot.active.${v}`),
    filter: {
      type: 'bool',
      group: t('pages.mountings.mounting.timeslots.filterGroups.general'),
      options: {
        label: t('mountingTimeslot.active.DEFAULT'),
        labelTrue: t('mountingTimeslot.active.true'),
        labelFalse: t('mountingTimeslot.active.false'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<MountingTimeslot>>('/mounting_timeslots', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const notify = useNotify();

const loading = computed(() => loadingTable.value || deleting.value);

const timeslotRegistrationColumns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
  },
  {
    key: 'timeslot.startTime',
    title: t('mountingTimeslotRegistration.startTime'),
  },
  {
    key: 'timeslot.duration',
    title: t('mountingTimeslot.duration'),
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
  },
  {
    key: 'driver.user.phoneNumber',
    title: t('user.phoneNumber'),
  },
  {
    key: 'driver.liabilityDisclaimerSigned',
    title: t('driver.liabilityDisclaimerSigned'),
    csvTranslations: {
      [true]: t('yes'),
      [false]: t('no'),
    }
  },
  {
    key: 'campaignRegistration.campaign.name',
    title: t('mountingTimeslotRegistration.campaign'),
  },
  {
    key: 'driver.box.type',
    csvTranslations: {
      null: t('driver.oldBoxVersion.null'),
      v1: t('driver.oldBoxVersion.v1'),
      v2: t('driver.oldBoxVersion.v2'),
    },
    title: t('driver.oldBoxVersion.DEFAULT'),
  },
  {
    key: 'driver.box.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: t('box.variant.DEFAULT'),
  },
  {
    key: 'type',
    csvTranslations: {
      mounting: t('mountingTimeslotRegistration.type.mounting'),
      unmounting: t('mountingTimeslotRegistration.type.unmounting'),
      change: t('mountingTimeslotRegistration.type.change'),
      fix: t('mountingTimeslotRegistration.type.fix'),
    },
    title: t('mountingTimeslotRegistration.type.DEFAULT'),
  },
  {
    key: 'driver.bicycleType',
    title: t('driver.bicycleType'),
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
  },
  {
    key: 'comment',
    title: t('mountingTimeslotRegistration.comment'),
  },
  {
    key: 'adminComment',
    title: t('mountingTimeslotRegistration.adminComment'),
  },
];

const visibleTimeslotRegistrationColumns = {
  id: true,
  'timeslot.startTime': true,
  'timeslot.duration': true,
  'driver.user.firstName': true,
  'driver.user.lastName': true,
  'driver.user.email': true,
  'driver.user.phoneNumber': true,
  'driver.liabilityDisclaimerSigned': true,
  'campaignRegistration.campaign.name': true,
  'driver.bicycleType': true,
  'driver.box.type': true,
  'driver.box.variant': true,
  type: true,
  comment: true,
  adminComment: true,
};
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

